import type { EliteAffix, EnemyDef, EnemyType } from './enemyDefs';
import type { ArtifactProc } from '../artifacts/ArtifactDefs';
import type { PhaseRunner } from '../bullets/patterns/PatternEmitter';

/** Windup/dash machine states, shared by husk and flanker. */
export type DashState = 'approach' | 'windup' | 'dash' | 'recover';

/**
 * "Special" = worth an active's attention over trash: any elite, plus the
 * boss-class types and the treasure goblin (Sarah's call — an aimed active
 * popping a calm goblin's panic clock from range is intended).
 */
export function isSpecialEnemy(e: Enemy): boolean {
  return e.affix !== null || e.type === 'boss' || e.type === 'keeper' || e.type === 'goblin';
}

export class Enemy {
  x = 0;
  y = 0;
  prevX = 0;
  prevY = 0;
  hp: number;
  maxHp: number;
  radius: number;
  speed: number;
  readonly contactDamage: number;
  gemValue: number;

  /** Elite state. */
  affix: EliteAffix | null = null;
  shieldHp = 0;
  /** Pre-rolled at floor start from the loot stream (kill order can't perturb). */
  dropArtifactId: string | null = null;
  dropQuality = 0;
  dropProc: ArtifactProc = 'none';
  dropCurseId: string | null = null;
  flash = 0;
  alive = true;

  /** Wither DoT (seconds remaining, damage/s). */
  witherLeft = 0;
  witherDps = 0;

  /**
   * Rime chill (seconds remaining): slowed (RIME_SLOW) and takes RIME_AMP
   * bonus damage at the damageEnemy funnel. chillShatter marks a Deep Freeze
   * chill: regulars below the shatter threshold die outright.
   */
  chillLeft = 0;
  chillShatter = false;

  // S2 debuff columns (same class as wither) — all applied/amplified at the
  // ONE damageEnemy funnel; skills that set them arrive in M7/M9.
  /** Brand mark (seconds remaining): amplified damage taken (BRAND_AMP). */
  brandLeft = 0;
  /** Fracture armor shred stacks (SHRED_PER_STACK each, cap SHRED_MAX_STACKS). */
  shredStacks = 0;
  /** Stun (seconds remaining): no movement or behavior. */
  stunLeft = 0;
  /** Burn DoT (Wick): seconds remaining + damage/s; feeds burn-detonation. */
  burnLeft = 0;
  burnDps = 0;
  /** Ground-zone slow, refreshed per tick while inside a slowing zone. */
  zoneSlowLeft = 0;
  zoneSlowMult = 1;
  /** Transient stamp: bullet kind of the shot currently resolving damage
   *  (−1 otherwise) — lets a corpse record what killed it (Shatterline). */
  lastHitKind = -1;

  /** Firing cycle (seeded pattern stream), null for melee types. */
  runner: PhaseRunner | null = null;
  fireCooldown = 0;
  /** Runner gate range from the def (default FIRE_RANGE). */
  readonly fireRange: number;

  // Windup/dash state machine (husk, flanker).
  state: DashState = 'approach';
  stateLeft = 0;
  dashVx = 0;
  dashVy = 0;

  // Watcher/flanker strafe direction (+1/-1), flipped occasionally by gameplay rng.
  strafeDir = 1;

  /** Leech heal-pulse countdown, seconds. 0 = unstarted (behavior initializes). */
  pulseLeft = 0;

  /** Goblin panic countdown, seconds. 0 = calm; >0 = fleeing; escapes at 0. */
  panicLeft = 0;
  /** Goblin calm-wander heading + reroll timer (gameplayRng, cosmetic class). */
  wanderAng = 0;
  wanderLeft = 0;

  constructor(
    readonly id: number,
    readonly type: EnemyType,
    def: EnemyDef,
    hpMult: number,
  ) {
    this.hp = this.maxHp = Math.round(def.hp * hpMult);
    this.radius = def.radius;
    this.speed = def.speed;
    this.contactDamage = def.contactDamage;
    this.gemValue = def.gemValue;
    this.fireRange = def.fireRange ?? 680;
  }

  place(x: number, y: number): void {
    this.x = this.prevX = x;
    this.y = this.prevY = y;
  }
}
